import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { SxProps } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Link, LinkProps } from 'react-router-dom';

import { Box } from './Box';
import { Button, ButtonProps } from './Button';
import { Icon } from './Icon';

export interface ButtonPropsWithLabel extends ButtonProps {
  label: string;
  icon?: any;
}

interface PageHeaderProps {
  title: React.ReactNode;
  titleVariant?: 'h4' | 'h5' | 'h6';
  actions?: React.ReactNode | ButtonPropsWithLabel[];
  row?: React.ReactNode;
  fixed?: boolean;
  backButtonTitle?: string;
  backButtonLinkTo?: LinkProps['to'] | (() => void);
  sx?: SxProps;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  titleVariant,
  actions,
  row,
  fixed,
  backButtonTitle,
  backButtonLinkTo,
  sx,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'pageHeader' });
  const hasBackButton = backButtonLinkTo && backButtonTitle;
  const isBackFunction = typeof backButtonLinkTo === 'function';
  const hasTitleOrBackButton = hasBackButton || title;

  let paddingTop = hasBackButton ? 4 : 6;
  if (!hasTitleOrBackButton) paddingTop = 0;
  const marginBottom = 4;

  const styles = [
    fixed && {
      position: 'sticky',
      top: '34px',
      left: 0,
      right: 0,
      bgcolor: (theme) => theme.vars.palette.background.default,
      zIndex: 50,
      backdropFilter: 'blur(10px)',
    },
    ...(Array.isArray(sx) ? sx : [sx]),
  ];

  return (
    <Box pt={paddingTop} mb={marginBottom} sx={styles}>
      {hasBackButton && (
        <Button
          component={isBackFunction ? 'button' : Link}
          to={isBackFunction ? undefined : backButtonLinkTo}
          color="info"
          startIcon={
            <Icon icon={faArrowLeft} />
          }
          sx={{ mb: 2 }}
          onClick={isBackFunction ? backButtonLinkTo : undefined}
        >
          {t('backTo', { title: backButtonTitle })}
        </Button>
      )}
      <Stack
        direction="row"
        spacing={4}
        sx={{ mb: 3, justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Typography
          sx={{ lineHeight: 'inherit' }}
          variant={titleVariant || 'h3'}
        >
          {title}
        </Typography>
        {!!actions && (
          <Stack
            spacing={2}
            direction="row"
            sx={{
              alignItems: 'center',
            }}
          >
            {Array.isArray(actions) &&
              actions.map((buttonProps, i) => (
                <Button
                  key={i}
                  startIcon={<Icon icon={buttonProps.icon} />}
                  {...buttonProps}
                >
                  {buttonProps.children || buttonProps.label}
                </Button>
              ))}
            {!Array.isArray(actions) && actions}
          </Stack>
        )}
      </Stack>
      {row && (
        <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
          {row}
        </Stack>
      )}
    </Box>
  );
};
